import { useState, useEffect, useRef } from 'react';

const TOOLTIP_WIDTH = 280;
const TOOLTIP_OFFSET = 16; // gap between target and tooltip
const ARROW_SIZE = 8;

/**
 * Find scrollable ancestors of an element (overflow: auto/scroll/overlay).
 */
function getScrollableAncestors(el) {
  const ancestors = [];
  let parent = el.parentElement;
  while (parent && parent !== document.body) {
    const style = window.getComputedStyle(parent);
    const overflowY = style.overflowY || style.overflow;
    if (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') {
      ancestors.push(parent);
    }
    parent = parent.parentElement;
  }
  return ancestors;
}

/**
 * Computes the position and arrow style for the tutorial tooltip,
 * anchored to the DOM element matching `targetSelector`.
 *
 * Polls for the element via MutationObserver (handles post-navigation rendering).
 * Updates position on scroll and resize.
 *
 * When step.preferPlacement is 'above' or 'below', that placement is used instead of the space-based heuristic.
 *
 * Returns null until the element is found.
 */
export function useTutorialPosition(targetSelector, step = null) {
  const [position, setPosition] = useState(null);
  const observerRef = useRef(null);
  const resizeObserverRef = useRef(null);
  const rafRef = useRef(null);
  const scrollableCleanupRef = useRef(() => {});

  const compute = (el) => {
    if (!el) {
      setPosition(null);
      return;
    }

    const rect = el.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    // Use step preferPlacement when set; otherwise determine by space
    const preferAbove = step?.preferPlacement === 'above';
    const preferBelow = step?.preferPlacement === 'below';
    const spaceBelow = vh - rect.bottom;
    const spaceAbove = rect.top;
    const placeBelow = preferBelow ? true : preferAbove ? false : (spaceBelow >= 120 || spaceBelow >= spaceAbove);

    // Horizontal: center tooltip over target, clamped within viewport
    let left = rect.left + rect.width / 2 - TOOLTIP_WIDTH / 2;
    left = Math.max(8, Math.min(left, vw - TOOLTIP_WIDTH - 8));

    let top;
    let placement;

    if (placeBelow) {
      top = rect.bottom + TOOLTIP_OFFSET;
      placement = 'below';
      top = Math.max(8, Math.min(top, vh - 120));
    } else {
      // Position above so the whole card stays lit and the tooltip sits entirely above it
      const estimatedTooltipHeight = 150;
      top = rect.top - TOOLTIP_OFFSET - estimatedTooltipHeight;
      placement = 'above';
      // Only clamp to avoid going off bottom; allow top to go to 0 or slightly off top so we don't push tooltip over the card
      top = Math.min(top, vh - estimatedTooltipHeight - 8);
      top = Math.max(-estimatedTooltipHeight + 20, top);
    }

    // Arrow points at horizontal center of target, relative to tooltip left
    const arrowLeft = rect.left + rect.width / 2 - left - ARROW_SIZE;
    const arrowClamped = Math.max(ARROW_SIZE, Math.min(arrowLeft, TOOLTIP_WIDTH - ARROW_SIZE * 3));

    setPosition({
      top,
      left,
      placement,
      arrowLeft: arrowClamped,
      targetRect: rect,
    });
  };

  useEffect(() => {
    if (!targetSelector) {
      setPosition(null);
      return;
    }

    let el = null;

    const onScroll = () => {
      if (el) rafRef.current = requestAnimationFrame(() => compute(el));
    };
    const onResize = () => {
      if (el) rafRef.current = requestAnimationFrame(() => compute(el));
    };

    const attachScrollableListeners = (element) => {
      scrollableCleanupRef.current();
      const scrollables = getScrollableAncestors(element);
      scrollables.forEach((node) => node.addEventListener('scroll', onScroll, true));
      scrollableCleanupRef.current = () => {
        scrollables.forEach((node) => node.removeEventListener('scroll', onScroll, true));
      };
    };

    const tryFind = () => {
      el = document.querySelector(targetSelector);
      if (el) {
        // Double rAF so we get the rect after layout is complete (avoids shifted highlight)
        requestAnimationFrame(() => {
          requestAnimationFrame(() => compute(el));
        });

        // Watch for size/position changes
        resizeObserverRef.current = new ResizeObserver(() => {
          rafRef.current = requestAnimationFrame(() => compute(el));
        });
        resizeObserverRef.current.observe(el);
        resizeObserverRef.current.observe(document.body);

        attachScrollableListeners(el);
      }
    };

    // Try immediately
    tryFind();

    // Then observe DOM mutations in case the element hasn't rendered yet
    observerRef.current = new MutationObserver(() => {
      if (!el) {
        tryFind();
      } else {
        rafRef.current = requestAnimationFrame(() => compute(el));
      }
    });
    observerRef.current.observe(document.body, { childList: true, subtree: true });

    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);

    return () => {
      observerRef.current?.disconnect();
      resizeObserverRef.current?.disconnect();
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
      scrollableCleanupRef.current();
      setPosition(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetSelector]);

  return position;
}
